import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactsService } from '../services/contacts.service';
import { colocService, Coloc } from '../services/coloc.service';
import { AgencyOverview } from '../types/contact';
import { getErrorMessage } from '../utils/error';
import '../styles/CandidatsPage.css';
import '../styles/ContactsAgency.css';
import '../styles/TenantsPage.css';

/**
 * LISTE des agences du bailleur — transverse, hors du sélecteur de colocation.
 *
 * Cet écran vit dans « Mon compte » : partager une colocation avec un tiers est
 * une décision de compte, pas un réglage par colocation.
 *
 * **Le détail est une PAGE, pas un repli.** Les lignes dépliables ont produit,
 * sur cet écran seul, quatre défauts : une confirmation écrasée dans une
 * cellule, quatre boutons qui débordaient sous 1200 px, des libellés qui
 * basculent (« Gérer » / « Fermer ») donc des colonnes qui se désalignent, et
 * une table imbriquée dans un `<td colSpan>`. Une page a de la place, une URL
 * partageable, et un bouton retour ; un repli n'a rien de tout cela.
 */
const AgencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [agences, setAgences] = useState<AgencyOverview[]>([]);
  const [colocs, setColocs] = useState<Coloc[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const [ouvert, setOuvert] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [colocInitiale, setColocInitiale] = useState('');
  // DÉCOCHÉE par défaut (2026-08-30) : créer un accès n'écrit à personne tant
  // qu'on ne le demande pas. Cochée d'avance, elle envoyait un courrier à un
  // compte qu'on préparait — et le geste par défaut doit être le plus discret.
  const [envoyerMaintenant, setEnvoyerMaintenant] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [liste, mesColocs] = await Promise.all([
        contactsService.listOwnerAgencies(),
        colocService.getColocs(),
      ]);
      setAgences(liste);
      setColocs(mesColocs);
      setErreur(null);
    } catch (err) {
      setErreur(getErrorMessage(err, 'Erreur lors du chargement des agences'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const handleCreer = async () => {
    setOccupe(true);
    setErreur(null);
    setInfo(null);
    try {
      const r = await contactsService.grantAgency(colocInitiale, {
        first_name: prenom.trim(),
        last_name: nom.trim(),
        email: email.trim(),
        send_welcome: envoyerMaintenant,
      });
      setOuvert(false);
      setPrenom('');
      setNom('');
      setEmail('');
      setEnvoyerMaintenant(false);
      // Le message dit ce qui s'est RÉELLEMENT passé : laisser croire à une
      // création alors que le compte existait ferait chercher un email de
      // bienvenue qui n'a pas lieu d'être.
      setInfo(
        r.created
          ? r.invitationSent
            ? `Accès accordé à ${r.email}. L'email de bienvenue est parti.`
            : `Accès accordé à ${r.email}. Aucun email envoyé — le bouton reste disponible sur sa fiche.`
          : `${r.email} avait déjà un compte agence : il a été réutilisé, pas recréé.`,
      );
      await charger();
    } catch (err) {
      setErreur(getErrorMessage(err, "Erreur lors de l'invitation"));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <h2>Agences</h2>
        <button
          className="btn-action btn-save"
          disabled={colocs.length === 0}
          onClick={() => {
            setOuvert(!ouvert);
            setColocInitiale(colocs[0]?.id ?? '');
          }}
        >
          {ouvert ? 'Annuler' : '+ Inviter une agence'}
        </button>
      </div>

      <p className="access-hint">
        Une agence consulte les dossiers de candidature des colocations que vous
        lui partagez, et peut y déposer des documents. Elle ne voit ni vos loyers,
        ni vos factures, ni vos charges.
      </p>

      {erreur && <div className="candidats-error">{erreur}</div>}
      {info && <div className="candidats-notice">{info}</div>}

      {ouvert && (
        <div className="cx-detail" style={{ marginBottom: 16 }}>
          <div className="cx-card">
            <h4 className="cx-title">Inviter une agence</h4>
            <div className="action-form-grid">
              <div className="action-field">
                <label htmlFor="ag-prenom">Prénom / Enseigne *</label>
                <input id="ag-prenom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
              </div>
              <div className="action-field">
                <label htmlFor="ag-nom">Nom *</label>
                <input id="ag-nom" value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="action-field">
                <label htmlFor="ag-email">Email *</label>
                <input id="ag-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="action-field">
                <label htmlFor="ag-coloc">Première colocation *</label>
                <select
                  id="ag-coloc"
                  value={colocInitiale}
                  onChange={(e) => setColocInitiale(e.target.value)}
                >
                  {colocs.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="access-hint" style={{ marginTop: 6 }}>
                  Vous pourrez lui en ajouter d'autres ensuite, depuis sa fiche.
                </p>
              </div>
              <div className="action-choix">
                <label>
                  <input
                    type="checkbox"
                    checked={envoyerMaintenant}
                    onChange={(e) => setEnvoyerMaintenant(e.target.checked)}
                  />
                  Envoyer tout de suite l'email de bienvenue
                </label>
                <p className="access-hint" style={{ marginTop: 6 }}>
                  Il contient un lien pour que l'agence choisisse son mot de passe —
                  vous ne le connaîtrez jamais. Sans cet envoi, le compte reste
                  inutilisable jusqu'à ce que vous le fassiez depuis sa fiche.
                  Si l'adresse est déjà celle d'une agence, son compte est réutilisé,
                  jamais dupliqué.
                </p>
              </div>
              <div className="cx-actions">
                <button
                  className="btn-action btn-save"
                  disabled={occupe || !prenom.trim() || !nom.trim() || !email.trim() || !colocInitiale}
                  onClick={handleCreer}
                >
                  {occupe ? 'Envoi…' : 'Inviter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {chargement ? (
        <div className="loading">Chargement...</div>
      ) : agences.length === 0 ? (
        <div className="socle-vide">
          <p className="socle-vide-titre">Aucune agence</p>
          <p className="socle-vide-aide">
            Invitez une agence pour qu'elle consulte vos dossiers de candidature et
            dépose les états des lieux, sans voir vos données financières.
          </p>
        </div>
      ) : (
        <div className="contacts-table-container">
          <table className="candidats-table">
            <thead>
              <tr>
                <th>Agence</th>
                <th>Email</th>
                <th>Colocations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agences.map((a) => {
                const actives = a.colocs.filter((c) => c.accessEnabled).length;
                return (
                  <tr
                    key={a.id}
                    className="candidats-row"
                    onClick={() => navigate(`/agences/${a.id}`)}
                  >
                    <td className="candidat-name">{a.name}</td>
                    <td>{a.email}</td>
                    <td>
                      {actives} / {a.colocs.length}
                      {actives < a.colocs.length && (
                        <span className="tenant-access-badge access-off" style={{ marginLeft: 8 }}>
                          {a.colocs.length - actives} suspendu(s)
                        </span>
                      )}
                    </td>
                    <td className="cx-actions">
                      {/* Un libellé FIXE : c'est en basculant entre « Gérer » et
                          « Fermer » que les colonnes se désalignaient. */}
                      <button
                        className="btn-action btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agences/${a.id}`);
                        }}
                      >
                        Gérer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgencesPage;
